/**
 * Ввод пин-кода: точки-индикаторы и цифровая клавиатура.
 *
 * Своя клавиатура, а не `<input type="password">`: системная в WebView прыгает,
 * перекрывает пол-экрана и показывает автозаполнение паролей, которое к пину
 * отношения не имеет. Здесь ввод предсказуемый и одинаковый на всех платформах.
 *
 * Компонент без состояния: значение и его сброс держит вызывающий — тот же
 * PinPad используется и для ввода, и для подтверждения нового пина.
 */

import { useTranslation } from 'react-i18next';

import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';

import { BackspaceIcon } from '../lib/icons';

import styles from './PinPad.module.css';

export interface PinPadProps {
  value: string;
  onChange: (value: string) => void;
  /** Сколько точек показывать. По умолчанию 4. */
  length?: number;
  title?: string;
  hint?: string;
  error?: string;
  /** Вызывается, когда набрана последняя цифра. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function PinPad({
  value,
  onChange,
  length = 4,
  title,
  hint,
  error,
  onComplete,
  disabled = false,
  className,
}: PinPadProps): React.ReactElement {
  const { t } = useTranslation();
  const { haptic } = useTelegram();

  const press = (digit: string): void => {
    if (disabled || value.length >= length) return;
    const next = value + digit;
    haptic('light');
    onChange(next);
    // Автоотправка на последней цифре: лишнее нажатие «ОК» на замке экрана
    // только раздражает.
    if (next.length === length) onComplete?.(next);
  };

  const backspace = (): void => {
    if (disabled || value.length === 0) return;
    haptic('light');
    onChange(value.slice(0, -1));
  };

  return (
    <div className={cn(styles.pad, className)}>
      {title !== undefined ? <p className={styles.title}>{title}</p> : null}

      <div className={styles.dots} role="status" aria-label={t('pin.entered', { count: value.length })}>
        {Array.from({ length }, (_, i) => (
          <span
            key={i}
            className={cn(
              styles.dot,
              i < value.length && styles.dotFilled,
              error !== undefined && styles.dotError,
            )}
          />
        ))}
      </div>

      {error !== undefined ? (
        <p className={styles.error}>{error}</p>
      ) : hint !== undefined ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}

      <div className={styles.keys}>
        {KEYS.map((digit) => (
          <button
            key={digit}
            type="button"
            className={styles.key}
            onClick={() => press(digit)}
            disabled={disabled}
          >
            {digit}
          </button>
        ))}
        <span className={styles.keySpacer} />
        <button type="button" className={styles.key} onClick={() => press('0')} disabled={disabled}>
          0
        </button>
        <button
          type="button"
          className={cn(styles.key, styles.keyAction)}
          onClick={backspace}
          disabled={disabled || value.length === 0}
          aria-label={t('pin.backspace')}
        >
          <BackspaceIcon size={20} />
        </button>
      </div>
    </div>
  );
}
